import { useCallback, useDebugValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncEntity } from 'src/global';
import { RootState } from 'src/store';
import { companyBannersSelectors, updateCompanyBanner } from 'src/features/companyBanners/companyBanners.state';
import { CompanyBanner } from 'src/features/companyBanners/companyBanners.types';
import { AppDispatch } from 'src/index';
import { useUploadFileToBucket } from 'src/hooks/useUploadFileToBucket';
import { uploadBannerImage } from 'src/features/companyBanners/companyBanners.api';

export const useCompanyBannersState = (): RootState['companyBanners'] => {
  const state = useSelector<RootState, RootState['companyBanners']>(state => state.companyBanners);

  useDebugValue(state);

  return state;
};

export const useCompanyBanners = (): CompanyBanner[] => {
  const banners = useSelector<RootState, RootState['companyBanners']>(state => state.companyBanners);

  const result = companyBannersSelectors.selectAll(banners);

  useDebugValue(result);

  return result;
};

export const useCompanyBannerById = (id: string): AsyncEntity<CompanyBanner> | null => {
  const banners = useSelector<RootState, RootState['companyBanners']>(state => state.companyBanners);

  const result = companyBannersSelectors.selectById(banners, id) || null;

  useDebugValue(result);

  return result;
};

export type UseBannerImage = (params: { bannerId: string; companyId: string }) => {
  progress: number;
  uploadBannerImage: (file: File) => void;
};

export const useBannerImage: UseBannerImage = ({ bannerId, companyId }) => {
  const dispatch: AppDispatch = useDispatch();
  const banner = useCompanyBannerById(bannerId) as CompanyBanner;
  const [progress, uploadFile] = useUploadFileToBucket();

  const updatePackageImage = useCallback(
    async (file: File) => {
      const { name: filename } = file;
      const images = banner.images || [];

      const credentials = await uploadBannerImage({
        bannerId,
        companyId,
        data: { filename },
      });

      const { url, fields } = credentials.data;
      const { key } = fields;

      if (credentials.status < 400) {
        const response = await uploadFile({ ...credentials.data, file });

        if (response) {
          console.log([...images, `${url}/${key}`]);
          dispatch(
            updateCompanyBanner({
              companyId,
              bannerId,
              data: { images: [...images, `${url}/${key}`] },
            })
          );
        }
      }
    },
    [companyId, dispatch, bannerId, uploadFile, banner]
  );

  return { progress, uploadBannerImage: updatePackageImage };
};
